import React, {useEffect, useRef} from 'react'
import {observer} from 'mobx-react'
import {Stack, Box, Flex, Center, Text} from '../../../vanilla'
import {SlotComponent} from '../../../types/ui'
import {CmsImage} from '../cms-image'
import {ImageGroup} from '../../../types/cms'
import {useSelectPromotionTracking} from '../../../analytics/promotion-tracking'
import {createPromoForSelectPromotion} from '../../../analytics/utils'
import {useViewPromotionTracking} from '../../../analytics/view-promotion-tracking'
import {Link} from '../../link'
import {
  brandFilterGridOneItem,
  brandFilterGridFewItems,
  brandFilterGridManyItems
} from './styles.css'

export interface BrandFilterContentProps {
  title?: string
  brandFilter: Array<{
    content: string
    contentId: string
    filterID: string
    image: ImageGroup
    brandName: string
  }>
}

export const BrandFilter: SlotComponent<BrandFilterContentProps> = observer(
  ({content, slotGroupName, slotIndex}) => {
    const {brandFilter, title} = content
    const sendSelectPromotion = useSelectPromotionTracking()
    const sendViewPromotion = useViewPromotionTracking()
    const brandFilterRefs = useRef(new Map<string, HTMLLIElement>())
    const brandPromotions = useRef(new Map<string, object>())
    const viewedBrandFilters = useRef(new Set<string>())

    // True View promo tracking
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          const visiblePromotions = entries.flatMap((entry) => {
            const brandFilterKey = entry.target.getAttribute('data-brand-filter-key')
            if (
              !entry.isIntersecting ||
              !brandFilterKey ||
              viewedBrandFilters.current.has(brandFilterKey)
            ) {
              return []
            }

            const promotion = brandPromotions.current.get(brandFilterKey)
            if (!promotion) return []

            viewedBrandFilters.current.add(brandFilterKey)
            observer.unobserve(entry.target)
            return [promotion]
          })

          if (visiblePromotions.length > 0) {
            sendViewPromotion(visiblePromotions)
          }
        },
        {rootMargin: '-70px 0px -70px 0px'}
      )

      brandFilterRefs.current.forEach((element) => observer.observe(element))
      return () => observer.disconnect()
    }, [brandFilter])

    const gridClassName =
      brandFilter?.length <= 1
        ? brandFilterGridOneItem
        : brandFilter?.length <= 4
        ? brandFilterGridFewItems
        : brandFilterGridManyItems

    return (
      <Stack gap="24px" data-test-selector="brand-filter-container">
        {/* Title */}
        {title && (
          <Text variant="heading1SemiBold" color="gray800">
            {title}
          </Text>
        )}

        <Box as="ul" display="grid" className={gridClassName}>
          {brandFilter?.map((_brandFilter, index) => {
            const {contentId, image, filterID, brandName} = _brandFilter
            const brandText = brandName ?? image.images[0].data.altText ?? ''
            const brandFilterKey = contentId || filterID

            const basePromotion = createPromoForSelectPromotion({
              content,
              slotGroupName,
              slotPosition: slotIndex
            })

            const brandPromotion = {
              ...basePromotion,
              promotion_id: filterID,
              promotion_name: brandText,
              link_position: index + 1
            }

            return (
              <Link
                key={brandFilterKey}
                to={filterID}
                style={{
                  display: 'contents'
                }}
                onClickCapture={() => {
                  sendSelectPromotion(brandPromotion)
                }}
              >
                <Flex
                  ref={(element: HTMLLIElement | null) => {
                    if (element) {
                      brandFilterRefs.current.set(brandFilterKey, element)
                      brandPromotions.current.set(brandFilterKey, brandPromotion)
                    } else {
                      brandFilterRefs.current.delete(brandFilterKey)
                      brandPromotions.current.delete(brandFilterKey)
                    }
                  }}
                  as="li"
                  data-brand-filter-key={brandFilterKey}
                  align="stretch"
                  gap="8px"
                  paddingX="8px"
                  backgroundColor={{default: 'white', hover: 'gray50'}}
                  borderRadius="medium"
                  boxShadow="base"
                  minWidth="0px"
                  height="70px"
                  data-test-selector="brand-filter"
                >
                  {/* Brand Name */}
                  <Flex
                    flex="1"
                    align="center"
                    justify="flex-start"
                    alignSelf="stretch"
                    minWidth="0px"
                    overflow="hidden"
                  >
                    <Text
                      variant="text3SemiBold"
                      width="full"
                      overflow="hidden"
                      style={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflowWrap: 'break-word'
                      }}
                    >
                      {brandText}
                    </Text>
                  </Flex>

                  {/* Brand Logo */}
                  <Center
                    width="74px"
                    height="62px"
                    flexShrink="0"
                    overflow="hidden"
                    borderTopLeftRadius="base"
                    borderTopRightRadius="base"
                    marginTop="8px"
                  >
                    <CmsImage
                      images={image.images}
                      imageLink={filterID}
                      style={{
                        width: 74,
                        height: 62,
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </Center>
                </Flex>
              </Link>
            )
          })}
        </Box>
      </Stack>
    )
  }
)
